import { normalizeTerm } from "./data";
import { categoryDisplayName, compareCategoriesNewestFirst } from "./categories";
import { deltaE2000, hslToLab } from "./color";
import type {
  ColorFilters,
  ColorSearchTarget,
  CoordEntry,
  CoordItem,
  FacetOption,
  NormalFilters,
  Swatch,
} from "./types";
import { COLOR_SEARCH_TARGETS } from "./types";

export const DEFAULT_COLOR_FILTERS: ColorFilters = {
  hslColors: [],
  colorMatchTolerance: 7,
  paletteRankLimit: 2,
  targets: COLOR_SEARCH_TARGETS.filter((target) => target !== "全身"),
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));
function validSwatches(swatches: Swatch[]): Swatch[] {
  return swatches.filter((swatch) => swatch?.lab?.length === 3);
}

function rankedSwatches(swatches: Swatch[], filters: ColorFilters): Swatch[] {
  return validSwatches(swatches).slice(0, clamp(filters.paletteRankLimit, 1, 5));
}

function targetFor(item: CoordItem): ColorSearchTarget | null {
  const term = normalizeTerm(item.term);
  return COLOR_SEARCH_TARGETS.includes(term as ColorSearchTarget)
    ? term as ColorSearchTarget
    : null;
}

function targetSelected(filters: ColorFilters, target: ColorSearchTarget | null): boolean {
  return target !== null && filters.targets.includes(target);
}

function selectedSwatches(entry: CoordEntry, filters: ColorFilters): Swatch[] {
  return [
    ...(targetSelected(filters, "全身") ? rankedSwatches(entry.colors?.palette ?? [], filters) : []),
    ...entry.items.flatMap((item) => targetSelected(filters, targetFor(item))
      ? rankedSwatches(item.colors?.palette ?? [], filters)
      : []),
  ];
}

function paletteContributes(swatches: Swatch[], filters: ColorFilters): boolean {
  if (!colorFiltersActive(filters)) return false;
  const tolerance = clamp(filters.colorMatchTolerance, 0, 20);
  const palette = rankedSwatches(swatches, filters);
  return filters.hslColors.some((selected) => {
    const selectedLab = hslToLab(selected);
    return palette.some((swatch) => deltaE2000(selectedLab, swatch.lab) <= tolerance);
  });
}

export function colorFiltersActive(filters: ColorFilters): boolean {
  return filters.hslColors.length > 0;
}

export function itemMatchesColor(item: CoordItem, filters: ColorFilters): boolean {
  return targetSelected(filters, targetFor(item))
    && paletteContributes(item.colors?.palette ?? [], filters);
}

export function fullImageMatchesColor(entry: CoordEntry, filters: ColorFilters): boolean {
  return targetSelected(filters, "全身")
    && paletteContributes(entry.colors?.palette ?? [], filters);
}

export function matchesColor(entry: CoordEntry, filters: ColorFilters): boolean {
  if (!colorFiltersActive(filters)) return true;
  return Number.isFinite(colorMatchScore(entry, filters));
}

export function colorMatchScore(entry: CoordEntry, filters: ColorFilters): number {
  if (!colorFiltersActive(filters)) return 0;
  const swatches = selectedSwatches(entry, filters);
  if (!swatches.length) return Number.POSITIVE_INFINITY;

  const tolerance = clamp(filters.colorMatchTolerance, 0, 20);
  const distances = filters.hslColors.map((selected) => {
    const selectedLab = hslToLab(selected);
    return Math.min(...swatches.map((swatch) => deltaE2000(selectedLab, swatch.lab)));
  });
  if (distances.some((distance) => distance > tolerance)) return Number.POSITIVE_INFINITY;
  return distances.reduce((total, distance) => total + distance, 0) / distances.length;
}

export function matchesEntry(
  entry: CoordEntry,
  filters: NormalFilters,
  colors: ColorFilters
): boolean {
  if (filters.brands.size && !filters.brands.has(entry.brand)) return false;
  if (filters.categories.size && !filters.categories.has(entry.category ?? "-")) return false;
  if (
    filters.terms.size &&
    !entry.items.some((item) => filters.terms.has(normalizeTerm(item.term)))
  ) return false;
  const query = filters.query.trim().toLocaleLowerCase("ja");
  if (
    query &&
    !entry.name.toLocaleLowerCase("ja").includes(query) &&
    !entry.items.some(
      (item) =>
        item.name.toLocaleLowerCase("ja").includes(query) ||
        item.id.toLocaleLowerCase("ja").includes(query)
    )
  ) return false;
  return matchesColor(entry, colors);
}

function facet(
  values: string[],
  options: {
    label?: (value: string) => string;
    compare?: (left: string, right: string) => number;
  } = {},
): FacetOption[] {
  const counts = new Map<string, number>();
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
  return [...counts]
    .map(([value, count]) => ({ value, count, ...(options.label ? { label: options.label(value) } : {}) }))
    .sort((a, b) => {
      if (options.compare) return options.compare(a.value, b.value);
      const numeric = Number(a.value) - Number(b.value);
      return Number.isNaN(numeric) ? a.value.localeCompare(b.value, "ja") : numeric;
    });
}

export function buildFacets(entries: CoordEntry[]) {
  return {
    brands: facet(entries.map((entry) => entry.brand)),
    terms: facet(entries.flatMap((entry) => entry.items.map((item) => normalizeTerm(item.term)))),
    categories: facet(entries.map((entry) => entry.category ?? "-"), {
      label: categoryDisplayName,
      compare: compareCategoriesNewestFirst,
    }),
  };
}
